// @ts-nocheck
import { useEffect, useState } from 'react';
import { List, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { c, Section, Field, Message, Check1, IconBtn, Modal, useStudio, useNode, reqTag, useShown } from '../ui';

const { USER_INSTRUCTION_MAX_ITEMS: MAX_ITEMS, USER_INSTRUCTION_MAX: MAX } = AI_AGENT_FIELD_LIMITS;
const TITLE_MAX = 100;

function InstructionModal({ open, initial, onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (open) {
      setTitle(initial?.title || '');
      setBody(initial?.body || '');
      setTried(false);
    }
  }, [open, initial]);

  const save = () => {
    setTried(true);
    if (!title.trim() || !body.trim()) return;
    onSave({ id: initial?.id, title: title.trim(), body });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? 'Edit instruction' : 'New instruction'}
      sub="A short rule for how the agent behaves on every call."
      footer={
        <>
          <button type="button" className={c('btn')} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={c('btn primary')} onClick={save}>
            Save instruction
          </button>
        </>
      }
    >
      <Field label="Name" required htmlFor="ags-i-title" error={tried && !title.trim() && 'Give this instruction a short name, like FAQ Flow.'}>
        <input
          id="ags-i-title"
          className={c('ctl', tried && !title.trim() && 'invalid')}
          value={title}
          maxLength={TITLE_MAX}
          placeholder="e.g. FAQ Flow"
          autoFocus
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field
        label="Instruction"
        required
        htmlFor="ags-i-body"
        count={body.length}
        max={MAX}
        note="Tell the agent what to do and how."
        error={tried && !body.trim() && 'Write what the agent should do.'}
      >
        <textarea
          id="ags-i-body"
          className={c('ctl', tried && !body.trim() && 'invalid')}
          rows={6}
          value={body}
          maxLength={MAX}
          placeholder="Refer to the uploaded knowledge base to understand how this product helps…"
          onChange={(e) => setBody(e.target.value.slice(0, MAX))}
        />
      </Field>
    </Modal>
  );
}

export default function InstructionsSection({ next, onNext, value: list, onChange }) {
  const { touch } = useStudio();
  const node = useNode('instructions');
  const shown = useShown();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const total = list.reduce((n, i) => n + (i.body?.length || 0), 0);
  const capacity = MAX_ITEMS * MAX;

  const openNew = () => {
    if (list.length >= MAX_ITEMS) {
      toast.error(`You can add up to ${MAX_ITEMS} instructions`);
      return;
    }
    setEditing(null);
    setOpen(true);
  };
  const save = (item) => {
    touch('instructions');
    if (item.id) onChange(list.map((i) => (i.id === item.id ? { ...i, ...item } : i)));
    else onChange([...list, { ...item, id: crypto.randomUUID(), enabled: true }]);
  };
  const toggle = (id) => {
    const cur = list.find((i) => i.id === id);
    if (cur?.enabled !== false && list.filter((i) => i.enabled !== false).length <= 1) {
      toast.error('At least one instruction must be selected');
      return;
    }
    touch('instructions');
    onChange(list.map((i) => (i.id === id ? { ...i, enabled: !i.enabled } : i)));
  };
  const remove = (id) => {
    touch('instructions');
    onChange(list.filter((i) => i.id !== id));
  };

  return (
    <Section
      id="instructions"
      icon={List}
      title="Instructions"
      sub="Short rules for tone and behaviour. Turn at least one on."
      tag={reqTag(node)}
      ok={node.ok}
      bad={node.bad}
      next={next('instructions')}
      onNext={onNext}
    >
      <div className={c('cnt')} style={{ margin: '0 0 4px' }}>
        <span>
          {list.length} of {MAX_ITEMS} instructions
        </span>
        <span>
          {total} of {capacity} characters
        </span>
      </div>
      <div className={c('meter')} aria-hidden="true">
        <i style={{ width: `${Math.min(100, (total / capacity) * 100)}%` }} />
      </div>
      <div style={{ height: 10 }} />

      {list.map((i) => (
        <div key={i.id} className={c('item', i.enabled === false && 'off')}>
          <Check1 checked={i.enabled !== false} label={`Use ${i.title}`} onChange={() => toggle(i.id)} />
          <div style={{ minWidth: 0 }}>
            <b>{i.title}</b> <span className={c('note')}>{i.body?.length || 0} of {MAX}</span>
            <p style={{ overflowWrap: 'anywhere' }}>{i.body}</p>
          </div>
          <div className={c('acts')}>
            <IconBtn
              icon={Pencil}
              label={`Edit ${i.title}`}
              onClick={() => {
                setEditing(i);
                setOpen(true);
              }}
            />
            <IconBtn icon={Trash2} danger label={`Delete ${i.title}`} onClick={() => remove(i.id)} />
          </div>
        </div>
      ))}
      <Message error={node.tone !== 'ok' && shown('instructions') && 'Turn on at least one instruction. It tells the agent how to behave.'} />
      <div className={c('addrow')}>
        <button type="button" className={c('btn small')} onClick={openNew} disabled={list.length >= MAX_ITEMS}>
          <Plus className="ags-i" />
          Add instruction
        </button>
      </div>
      <InstructionModal open={open} initial={editing} onClose={() => setOpen(false)} onSave={save} />
    </Section>
  );
}
